import { useCallback } from "react";
import { Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface UploadDropzoneProps {
  onFileUpload: (file: File) => void;
  isUploading: boolean;
}

export function UploadDropzone({ onFileUpload, isUploading }: UploadDropzoneProps) {
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFileUpload(files[0]);
      }
    },
    [onFileUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileUpload(files[0]);
      }
    },
    [onFileUpload]
  );

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className={cn(
        "border-2 border-dashed border-muted-foreground/30 rounded-xl p-8",
        "hover:border-muted-foreground/50 transition-colors duration-200",
        "bg-ios-surface-secondary/50"
      )}
    >
      <div className="text-center">
        <div className="mb-4">
          <div className={cn(
            "w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto",
            isUploading && "animate-pulse"
          )}>
            {isUploading ? (
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Upload size={24} className="text-primary" />
            )}
          </div>
        </div>
        
        <h3 className="ios-headline text-foreground mb-2">
          {isUploading ? "Uploading..." : "Upload Files"}
        </h3>
        
        <p className="ios-body text-muted-foreground mb-4">
          {isUploading 
            ? "Please wait while we process your file" 
            : "Drag and drop files here or click to browse"
          }
        </p>
        
        <input
          type="file"
          onChange={handleFileSelect}
          disabled={isUploading}
          className="hidden"
          id="file-upload"
          accept=".txt,.md"
        />
        
        <label htmlFor="file-upload">
          <Button
            variant="default"
            disabled={isUploading}
            className={cn(
              "h-12 px-6 rounded-lg",
              "active:scale-95 transition-all duration-200"
            )}
            asChild
          >
            <span className="cursor-pointer">
              <Plus size={20} className="mr-2" />
              Choose Files
            </span>
          </Button>
        </label>
        
        <div className="mt-3 text-xs text-muted-foreground">
          Supports TXT and MD files only
        </div>
      </div>
    </div>
  );
}